
import { useState, useEffect, useRef } from 'react';
import { Lead } from '@/types/lead';
import { User } from '@supabase/supabase-js';
import { LeadFilters } from '@/types/filters';

const FILTERS_STORAGE_KEY = 'leadFilters';

const getStoredFilters = (companyId: string): LeadFilters | null => {
  try {
    const storedData = localStorage.getItem(`${FILTERS_STORAGE_KEY}_${companyId}`);
    return storedData ? JSON.parse(storedData) : null;
  } catch (error) {
    console.error('Error reading stored filters:', error);
    return null;
  }
};

const saveFilters = (companyId: string, filters: LeadFilters) => {
  try {
    localStorage.setItem(`${FILTERS_STORAGE_KEY}_${companyId}`, JSON.stringify(filters));
  } catch (error) {
    console.error('Error saving filters:', error);
  }
};

export const useLeadsFilter = (leads: Lead[], user: User | null, companyId?: string) => {
  const hasHydratedRef = useRef(false);
  
  // Hydrate immediately from localStorage or use defaults
  const storedFilters = companyId ? getStoredFilters(companyId) : null;
  const [filteredLeads, setFilteredLeads] = useState<Lead[]>([]);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(storedFilters?.showFavoritesOnly ?? false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(storedFilters?.selectedCategory ?? null);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(storedFilters?.selectedUserId ?? 'my-leads');
  const [currentView, setCurrentView] = useState<'card' | 'list' | 'table'>(storedFilters?.currentView ?? 'card');

  // Mark as hydrated on first render
  useEffect(() => {
    hasHydratedRef.current = true;
  }, []);

  // Filter leads based on current filter settings
  useEffect(() => {
    const applyFilters = () => {
      if (!leads.length) {
        setFilteredLeads([]);
        return;
      }

      let filtered = [...leads];
      
      if (showFavoritesOnly) {
        filtered = filtered.filter(lead => lead.isFavorite);
      }
      
      if (selectedCategory) {
        filtered = filtered.filter(lead => lead.category === selectedCategory);
      }
      
      if (selectedUserId === 'my-leads' && user) {
        filtered = filtered.filter(lead => lead.createdBy === user.id);
      } else if (selectedUserId) {
        filtered = filtered.filter(lead => lead.createdBy === selectedUserId);
      }
      
      setFilteredLeads(filtered);
    };
    
    applyFilters();
    
    if (Array.isArray(leads) && leads.length > 0) {
      const uniqueCategories = Array.from(
        new Set(
          leads
            .filter(lead => Boolean(lead.category))
            .map(lead => lead.category as string)
        )
      ).sort();
      
      setCategories(uniqueCategories);
    } else {
      setCategories([]);
    }
  }, [leads, showFavoritesOnly, selectedCategory, selectedUserId, user]);

  // Save filters when they change (but only after hydration)
  useEffect(() => {
    if (companyId && hasHydratedRef.current) {
      saveFilters(companyId, {
        showFavoritesOnly,
        selectedCategory,
        selectedUserId,
        currentView
      });
    }
  }, [showFavoritesOnly, selectedCategory, selectedUserId, currentView, companyId]);

  return {
    filteredLeads,
    showFavoritesOnly,
    setShowFavoritesOnly,
    selectedCategory,
    setSelectedCategory,
    categories,
    selectedUserId,
    setSelectedUserId,
    currentView,
    setCurrentView
  };
};
